import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import {
  ClipboardDocumentListIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  EyeIcon,
  FunnelIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  WrenchScrewdriverIcon,
  CurrencyDollarIcon,
  UserIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import apiClient from '../api/config';
import JobCardWorkflowModal from '../components/jobcard/JobCardWorkflowModal';
import CustomerCheckInModal from '../components/modals/CustomerCheckInModal';
import { invoiceAPI } from '../services/invoiceAPI';

interface JobCard {
  id: number;
  job_number: string;
  vehicle_id: number;
  vehicle?: {
    make: string;
    model: string;
    plate_number: string;
  };
  customer_id: number;
  customer?: {
    name: string;
    phone_number: string;
  };
  assigned_to?: number;
  assigned_employee?: {
    name: string;
  };
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high';
  service_type: string;
  description: string;
  estimated_cost: number;
  actual_cost: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
}

interface JobCardStats {
  status_counts: {
    pending: number;
    in_progress: number;
    completed: number;
    cancelled: number;
  };
  today_count: number;
  month_revenue: number;
  avg_completion_hours: number;
}

const JobCardsPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [jobCards, setJobCards] = useState<JobCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [selectedJobCard, setSelectedJobCard] = useState<JobCard | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [stats, setStats] = useState<JobCardStats | null>(null);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    assigned_to: ''
  });
  const [formData, setFormData] = useState({
    vehicle_id: '',
    customer_id: '',
    service_type: '',
    description: '',
    priority: 'medium',
    estimated_cost: 0,
    mileage_in: ''
  });

  useEffect(() => {
    fetchJobCards();
    fetchStats();
  }, [currentPage, searchTerm, filters]);

  const fetchJobCards = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        page_size: 20,
        search: searchTerm
      };
      
      // Add filters
      if (filters.status) params.status = filters.status;
      if (filters.priority) params.priority = filters.priority;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;

      const response = await apiClient.get('/job-cards', { params });
      setJobCards(response.data.job_cards || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      console.error('Error fetching job cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiClient.get('/job-cards/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchJobCardDetails = async (jobCardId: number) => {
    try {
      const response = await apiClient.get(`/job-cards/${jobCardId}`);
      setSelectedJobCard(response.data.job_card);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error fetching job card details:', error);
    }
  };

  const handleWorkflowModal = (jobCard: JobCard) => {
    setSelectedJobCard(jobCard);
    setShowWorkflowModal(true);
  };

  const handleWorkflowChange = () => {
    fetchJobCards();
    fetchStats();
  };

  const handleCreateInvoice = async (jobCardId: number) => {
    try {
      const response = await invoiceAPI.createInvoiceFromJobCard(jobCardId);
      alert(`Invoice created successfully! Invoice Number: ${response.data.invoice_number}`);
      fetchJobCards(); // Refresh to update any status changes
    } catch (error) {
      console.error('Error creating invoice from job card:', error);
      alert('Failed to create invoice. Please try again.');
    }
  };

  const handleVehicleSelected = (customer: any, vehicle: any) => {
    setSelectedCustomer(customer);
    setSelectedVehicle(vehicle);
    setFormData({
      ...formData,
      vehicle_id: vehicle.id.toString(),
      customer_id: customer.id.toString()
    });
    setShowCheckInModal(false);
    setShowAddModal(true);
  };

  const handleCreateJobCard = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.post('/job-cards', {
        ...formData,
        vehicle_id: parseInt(formData.vehicle_id),
        customer_id: parseInt(formData.customer_id),
        estimated_cost: parseFloat(formData.estimated_cost.toString()),
        mileage_in: formData.mileage_in ? parseInt(formData.mileage_in) : null
      });
      setShowAddModal(false);
      setSelectedCustomer(null);
      setSelectedVehicle(null);
      resetForm();
      fetchJobCards();
      fetchStats();
    } catch (error) {
      console.error('Error creating job card:', error);
    }
  };

  const handleUpdateStatus = async (jobCardId: number, newStatus: string) => {
    try {
      await apiClient.put(`/job-cards/${jobCardId}`, {
        status: newStatus
      });
      fetchJobCards();
      fetchStats();
    } catch (error) {
      console.error('Error updating job card status:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_id: '',
      customer_id: '',
      service_type: '',
      description: '',
      priority: 'medium',
      estimated_cost: 0,
      mileage_in: ''
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      case 'in_progress':
        return <WrenchScrewdriverIcon className="h-5 w-5 text-blue-500" />;
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'cancelled':
        return <XMarkIcon className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getPriorityBadge = (priority: string) => {
    const classes = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-100 text-red-800'
    };
    return classes[priority as keyof typeof classes] || classes.medium;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className={`container mx-auto px-4 py-8 ${language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('pendingJobs')}</p>
                <p className="text-2xl font-bold">{stats.status_counts.pending || 0}</p>
              </div>
              <ClockIcon className="h-8 w-8 text-yellow-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('inProgress')}</p>
                <p className="text-2xl font-bold">{stats.status_counts.in_progress || 0}</p>
              </div>
              <WrenchScrewdriverIcon className="h-8 w-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('todaysJobs')}</p>
                <p className="text-2xl font-bold">{stats.today_count || 0}</p>
              </div>
              <ClipboardDocumentListIcon className="h-8 w-8 text-teal-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">{t('monthRevenue')}</p>
                <p className="text-2xl font-bold">KD {stats.month_revenue || 0}</p>
              </div>
              <CurrencyDollarIcon className="h-8 w-8 text-green-500" />
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center">
            <ClipboardDocumentListIcon className="h-8 w-8 mr-2 text-teal-600" />
            {t('jobCardManagement')}
          </h1>
          <button
            onClick={() => setShowCheckInModal(true)}
            className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            {t('createJobCard')}
          </button>
        </div>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder={t('searchJobCards')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <button className="px-4 py-2 border rounded-lg hover:bg-gray-50 flex items-center">
              <FunnelIcon className="h-5 w-5 mr-2" />
              {t('filters')}
            </button>
          </div>

          {/* Quick Filters */}
          <div className="flex gap-2">
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              className="px-3 py-1 border rounded-md text-sm"
            >
              <option value="">{t('allStatuses')}</option>
              <option value="pending">{t('pending')}</option>
              <option value="in_progress">{t('inProgress')}</option>
              <option value="completed">{t('completed')}</option>
              <option value="cancelled">{t('cancelled')}</option>
            </select>
            <select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
              className="px-3 py-1 border rounded-md text-sm"
            >
              <option value="">{t('allPriorities')}</option>
              <option value="low">{t('low')}</option>
              <option value="medium">{t('medium')}</option>
              <option value="high">{t('high')}</option>
            </select>
          </div>
        </div>

        {/* Job Cards Table */}
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loading')}</p>
          </div>
        ) : jobCards.length === 0 ? (
          <div className="text-center py-8">
            <ClipboardDocumentListIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('noJobCardsFound')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('jobNumber')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('customer')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('vehicle')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('service')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('status')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('priority')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('assignedTo')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {jobCards.map((jobCard) => (
                  <tr key={jobCard.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(jobCard.status)}
                        <span className="ml-2 text-sm font-medium text-gray-900">
                          {jobCard.job_number}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {jobCard.customer?.name || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {jobCard.customer?.phone_number || ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {jobCard.vehicle?.make} {jobCard.vehicle?.model}
                      </div>
                      <div className="text-sm text-gray-500">
                        {jobCard.vehicle?.plate_number}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {jobCard.service_type}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-xs">
                        {jobCard.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={jobCard.status}
                        onChange={(e) => handleUpdateStatus(jobCard.id, e.target.value)}
                        className="text-sm border rounded px-2 py-1"
                      >
                        <option value="pending">{t('pending')}</option>
                        <option value="in_progress">{t('inProgress')}</option>
                        <option value="completed">{t('completed')}</option>
                        <option value="cancelled">{t('cancelled')}</option>
                      </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityBadge(jobCard.priority)}`}>
                        {t(jobCard.priority)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {jobCard.assigned_employee?.name || t('unassigned')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => fetchJobCardDetails(jobCard.id)}
                          className="text-teal-600 hover:text-teal-900"
                          title="View Details"
                        >
                          <EyeIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleWorkflowModal(jobCard)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Manage Workflow"
                        >
                          <ArrowPathIcon className="h-5 w-5" />
                        </button>
                        {jobCard.status === 'completed' && (
                          <button
                            onClick={() => handleCreateInvoice(jobCard.id)}
                            className="text-green-600 hover:text-green-900"
                            title="Create Invoice"
                          >
                            <CurrencyDollarIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                {t('previous')}
              </button>
              <span className="px-4 py-1">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                {t('next')}
              </button>
            </nav>
          </div>
        )}
      </div>

      {/* Customer Check-In Modal */}
      <CustomerCheckInModal
        isOpen={showCheckInModal}
        onClose={() => setShowCheckInModal(false)}
        onSelectVehicle={handleVehicleSelected}
      />

      {/* Create Job Card Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">{t('createJobCard')}</h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleCreateJobCard}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {selectedCustomer && selectedVehicle && (
                  <div className="md:col-span-2 mb-4 p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold mb-2">{t('selectedCustomerVehicle')}</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">{t('customer')}:</span>
                        <p className="font-medium">{selectedCustomer.name}</p>
                        <p className="text-gray-500">{selectedCustomer.phone_number}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">{t('vehicle')}:</span>
                        <p className="font-medium">{selectedVehicle.year} {selectedVehicle.make} {selectedVehicle.model}</p>
                        <p className="text-gray-500">{selectedVehicle.plate_number}</p>
                      </div>
                    </div>
                  </div>
                )}

                <input type="hidden" value={formData.vehicle_id} />
                <input type="hidden" value={formData.customer_id} />

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('serviceType')}
                  </label>
                  <input
                    type="text"
                    value={formData.service_type}
                    onChange={(e) => setFormData({ ...formData, service_type: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    required
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('description')}
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('priority')}
                  </label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="low">{t('low')}</option>
                    <option value="medium">{t('medium')}</option>
                    <option value="high">{t('high')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('estimatedCost')} (KD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.estimated_cost}
                    onChange={(e) => setFormData({ ...formData, estimated_cost: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('mileageIn')} (km)
                  </label>
                  <input
                    type="number"
                    value={formData.mileage_in}
                    onChange={(e) => setFormData({ ...formData, mileage_in: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSelectedCustomer(null);
                    setSelectedVehicle(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700"
                >
                  {t('createJobCard')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Workflow Modal */}
      {showWorkflowModal && selectedJobCard && (
        <JobCardWorkflowModal
          jobCard={selectedJobCard}
          isOpen={showWorkflowModal}
          onClose={() => {
            setShowWorkflowModal(false);
            setSelectedJobCard(null);
          }}
          onStatusChange={handleWorkflowChange}
        />
      )}
    </div>
  );
};

export default JobCardsPage;